/**
 * Filter row of the Discover Workspaces page (Figma 10219:24142): search by
 * workspace name, type-to-filter "Filter by user name" and "Filter by owner"
 * comboboxes, the "Filter by status" select, and the grid / list toggle.
 * The page owns the filter state; this component only renders Spark inputs.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
    DropdownMenu,
    Search,
    SegmentedControlIcon,
    TABLE_VIEW_TOGGLE_ITEMS,
    TooltipTrigger,
    TruncatedText,
    isTextOverflowing,
} from '@spark';
import { STATUS_FILTERS } from '../../utils/discoverFilters';

export const SEARCH_PLACEHOLDER = 'Search by workspace name';
export const USER_FILTER_PLACEHOLDER = 'Filter by user name';
export const OWNER_FILTER_PLACEHOLDER = 'Filter by owner';
export const STATUS_FILTER_PLACEHOLDER = 'Filter by status';

/**
 * Dropdown rows for a people list.  Two people with the same display name get
 * their email appended so the selected label maps back to exactly one person.
 * `searchText` (label + email) is unique per person: it feeds the trigger
 * search and identifies the clicked row, since the rendered label is a node.
 */
export function peopleItems(options) {
    const counts = new Map();
    options.forEach(({ label }) => counts.set(label, (counts.get(label) || 0) + 1));
    return options.map(({ email, label }) => ({
        email,
        label: counts.get(label) > 1 && label !== email ? `${label} (${email})` : label,
        searchText: `${label} ${email}`,
    }));
}

function PersonFilter({ placeholder, options, value, onChange, testId }) {
    const items = useMemo(() => peopleItems(options), [options]);
    const selected = items.find((item) => item.email === value);
    // The selected "Name (email)" can be cut by the field too; measured on hover so
    // the tooltip only appears when the ellipsis is actually showing.
    const fieldRef = useRef(null);
    const [valueClipped, setValueClipped] = useState(false);
    const measureValue = () => {
        const valueEl = fieldRef.current?.querySelector('.spark-input-select-value');
        setValueClipped(Boolean(valueEl) && isTextOverflowing(valueEl));
    };
    return (
        <div
            ref={fieldRef}
            className="discover-toolbar__field discover-toolbar__field--person"
            data-testid={testId}
            onMouseEnter={measureValue}
        >
            <TooltipTrigger
                text={selected?.label || ''}
                disabled={!selected || !valueClipped}
                className="discover-toolbar__value-tip"
            >
                <DropdownMenu
                    fill
                    showLabel={false}
                    placeholder={placeholder}
                    searchable
                    searchMode="trigger"
                    searchEmptyText="No matching people"
                    // A long "Name (email)" row ellipsizes at the field width; the tooltip
                    // carries the full value only when it is actually cut.
                    items={items.map(({ email, label, searchText }) => ({
                        label: <TruncatedText text={label} maxWidth="100%" />,
                        searchText,
                        state: email === value ? 'selected' : 'default',
                    }))}
                    value={selected?.label || undefined}
                    onSelect={(item) => {
                        const match = items.find((candidate) => candidate.searchText === item.searchText);
                        onChange(match?.email || '');
                    }}
                    showClearButton
                    onClear={() => onChange('')}
                    disabled={items.length === 0}
                />
            </TooltipTrigger>
        </div>
    );
}

export default function DiscoverToolbar({
    filters,
    onFiltersChange,
    peopleOptions: people = [],
    ownerOptions: owners = [],
    view = 'grid',
    onViewChange,
}) {
    const update = (patch) => onFiltersChange({ ...filters, ...patch });
    const statusItems = STATUS_FILTERS.map(({ label }) => ({ label }));
    // No status filter reads as the placeholder; an explicit status shows as the value.
    const statusLabel = STATUS_FILTERS.find((option) => option.id === filters.status)?.label;

    return (
        <div className="discover-toolbar" data-testid="discover-toolbar">
            <div className="discover-toolbar__filters">
                <div className="discover-toolbar__field discover-toolbar__field--search">
                    <Search
                        size="medium"
                        iconPosition="start"
                        placeholder={SEARCH_PLACEHOLDER}
                        aria-label={SEARCH_PLACEHOLDER}
                        value={filters.query}
                        onChange={(event) => update({ query: event.target.value })}
                        showClearButton
                        onClear={() => update({ query: '' })}
                        data-testid="discover-search"
                    />
                </div>
                <PersonFilter
                    placeholder={USER_FILTER_PLACEHOLDER}
                    options={people}
                    value={filters.userEmail}
                    onChange={(userEmail) => update({ userEmail })}
                    testId="discover-filter-user"
                />
                <PersonFilter
                    placeholder={OWNER_FILTER_PLACEHOLDER}
                    options={owners}
                    value={filters.ownerEmail}
                    onChange={(ownerEmail) => update({ ownerEmail })}
                    testId="discover-filter-owner"
                />
                <div className="discover-toolbar__field discover-toolbar__field--status" data-testid="discover-filter-status">
                    <DropdownMenu
                        fill
                        showLabel={false}
                        placeholder={STATUS_FILTER_PLACEHOLDER}
                        items={statusItems}
                        value={statusLabel}
                        onSelect={(item) => {
                            const option = STATUS_FILTERS.find((candidate) => candidate.label === item.label);
                            update({ status: option?.id || 'all' });
                        }}
                        showClearButton
                        onClear={() => update({ status: 'all' })}
                    />
                </div>
            </div>
            <SegmentedControlIcon
                items={TABLE_VIEW_TOGGLE_ITEMS}
                activeId={view}
                onChange={onViewChange}
                className="discover-toolbar__view"
            />
        </div>
    );
}
